import classNames from 'classnames';
import isTouchDevice from 'is-touch-device';
import { map, get, forEach, memoize, reduce } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Carousel from 'components/Carousel';
import { QuaternaryHeading } from 'components/Headings';
import ProductCard from 'components/ProductCard';
import { breakpoints, mediumUp, small } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

import NavigationArrow from './NavigationArrow';

const SLIDES_PER_VIEWPORT = {
  SMALL: 1,
  MEDIUM: 2,
};

const BOTTOM_PADDING_SMALL = Theme.get('spacing') * 12;
const BOTTOM_PADDING_MEDIUM = Theme.get('spacing') * 13;

class MobileCarousel extends React.Component {
  carouselElementCount = memoize(categories => {
    return reduce(
      categories,
      (sum, category) => {
        const categoryCardsLength = get(category, ['categoryCards', 'length'], 0);
        return sum + categoryCardsLength;
      },
      0
    );
  }, JSON.stringify);

  constructor(props) {
    super(props);

    this.mobileHeaderRef = React.createRef();
  }

  onCategorySelect = index => {
    const { handleCategorySelect } = this.props;

    this.scrollToActiveHeader(index);
    handleCategorySelect(index);
  };

  onSlideSelect = slideIndex => {
    const { activeSlideIndex, handleSlideSelect } = this.props;
    if (slideIndex === activeSlideIndex) return;

    const newCategoryIndex = this.findCategoryIndexBySlideIndex(slideIndex);

    this.scrollToActiveHeader(newCategoryIndex);
    handleSlideSelect(slideIndex, newCategoryIndex);
  };

  findCategoryIndexBySlideIndex = slideIndex => {
    const { categories } = this.props;

    let newCategoryIndex = 0;
    let testSlideIndex = 0;

    forEach(categories, category => {
      testSlideIndex += get(category, ['categoryCards', 'length'], 0);

      if (testSlideIndex <= slideIndex) {
        newCategoryIndex += 1;
      }
    });

    return newCategoryIndex;
  };

  scrollToActiveHeader = categoryIndex => {
    if (!this.mobileHeaderRef.current) {
      return;
    }

    const containerElement = this.mobileHeaderRef.current;
    const overflowContainer = containerElement.querySelector('.MobileCarousel__category-container');
    const activeChildElement = overflowContainer.children[categoryIndex];

    // find half width of viewport
    const centerViewPoint = containerElement.clientWidth / 2;

    // find half of elements length
    const centerElementLength = activeChildElement.clientWidth / 2;

    // subtract half of the elements length from 50% of screen size.
    // this will give an offsetLeft that will center the element
    const adjustedCenter = centerViewPoint - centerElementLength;

    // set the offsetLeft to be the adjusted offset left
    containerElement.scrollLeft = activeChildElement.offsetLeft - adjustedCenter;
  };

  render() {
    const { categories, trackCTAClick, activeCategoryIndex, activeSlideIndex } = this.props;
    const totalSlidesCount = this.carouselElementCount(categories);

    return (
      <div className="MobileCarousel">
        <div className="MobileCarousel__category-container-viewport" ref={this.mobileHeaderRef}>
          <div className="MobileCarousel__category-container">
            {map(categories, (category, index) => (
              <button
                className={classNames('MobileCarousel__category', {
                  'MobileCarousel__category--active': index === activeCategoryIndex,
                })}
                aria-label={I18n.localize('carousel.viewSlide', { slideIndex: index })}
                onClick={() => this.onCategorySelect(index)}
                key={`mobile-category:${index}`}
              >
                <QuaternaryHeading key={`mobile-heading:${index}`}>
                  {get(category, 'categoryName')}
                </QuaternaryHeading>
              </button>
            ))}
          </div>
        </div>
        <div className="MobileCarousel__carousel-container">
          <Carousel
            key="base-carousel-homepage-v2"
            currentSlide={activeSlideIndex}
            options={{
              perPage: {
                [breakpoints.small.min]: 1,
                [breakpoints.medium.min]: 2,
              },
              loop: false,
            }}
            onChange={currentSlide => this.onSlideSelect(currentSlide)}
          >
            {map(categories, (category, categoryIndex) => {
              const productCards = get(category, 'categoryCards');

              return map(productCards, (productCard, productIndex) => (
                <div
                  className="MobileCarousel__product"
                  key={`mobile-product-${categoryIndex}:${productIndex}`}
                >
                  <ProductCard
                    {...productCard}
                    trackCTAClick={trackCTAClick}
                    productCardIndex={productIndex}
                    categoryIndex={categoryIndex}
                  />
                </div>
              ));
            })}
          </Carousel>
          {!isTouchDevice() && (
            <>
              <div
                className={classNames('MobileCarousel__left-arrow', {
                  'MobileCarousel__left-arrow--active': activeSlideIndex > 0,
                })}
              >
                <NavigationArrow
                  direction="left"
                  aria-label={I18n.localize('carousel.viewPreviousSlide')}
                  onClick={() => this.onSlideSelect(activeSlideIndex - 1)}
                />
              </div>
              <div
                className={classNames('MobileCarousel__right-arrow', {
                  'MobileCarousel__right-arrow--active-small':
                    activeSlideIndex + SLIDES_PER_VIEWPORT.SMALL < totalSlidesCount,
                  'MobileCarousel__right-arrow--active-medium':
                    activeSlideIndex + SLIDES_PER_VIEWPORT.MEDIUM < totalSlidesCount,
                })}
              >
                <NavigationArrow
                  direction="right"
                  aria-label={I18n.localize('carousel.viewNextSlide')}
                  onClick={() => this.onSlideSelect(activeSlideIndex + 1)}
                />
              </div>
            </>
          )}
        </div>
        <style jsx>{`
          .MobileCarousel__product {
            margin: 0 ${Theme.get('spacing')}px;
            height: 100%;
          }

          .MobileCarousel__category-container-viewport {
            overflow-x: scroll;
            cursor: grab;
            scroll-behavior: smooth;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
          }

          /* Hide scrollbar for Chrome, Safari and Opera */
          .MobileCarousel__category-container-viewport::-webkit-scrollbar {
            display: none;
          }

          .MobileCarousel__category-container {
            display: inline-flex;
            min-width: 100%;
            justify-content: center;
            position: relative;
            margin: auto;
            margin-bottom: ${Theme.get('spacing') * 4}px;
            padding: 0 ${Theme.get('spacing') * 6}px;
            border-bottom: solid 2px ${Theme.get('colors-black-20')};
          }

          .MobileCarousel__category {
            opacity: 0.8;
            padding: ${Theme.get('spacing') * 5}px ${Theme.get('spacing') * 3}px
              ${Theme.get('spacing')}px;
            background-color: transparent;
            transform: translateY(2px);
            cursor: pointer;
            border: none;
            white-space: nowrap;
            flex-shrink: 0;
            outline: inherit;
            border-bottom: solid 2px transparent;
            margin: 0;
          }

          .MobileCarousel__category.focused {
            outline: 2px solid ${Theme.get('colors-black')};
          }

          @media ${mediumUp} {
            .MobileCarousel__category {
              margin: 0 ${Theme.get('spacing') * 2}px;
            }

            .MobileCarousel__category-container {
              padding: 0 ${Theme.get('spacing') * 4}px;
            }
          }

          .MobileCarousel__category--active {
            opacity: 1;
            transition: opacity ${Theme.get('transition-duration')}ms ease;
            border-bottom-color: ${Theme.get('colors-black')};
          }

          .MobileCarousel__carousel-container {
            padding: 0 ${Theme.get('spacing') * 5}px;
            position: relative;
          }

          .MobileCarousel__left-arrow,
          .MobileCarousel__right-arrow {
            position: absolute;
            top: calc(50% - ${BOTTOM_PADDING_SMALL / 2}px);
            transform: translateY(-50%);
            opacity: 0;
            visibility: hidden;
            transition: opacity ${Theme.get('transition-duration') / 2}ms ease;
          }

          @media ${mediumUp} {
            .MobileCarousel__left-arrow,
            .MobileCarousel__right-arrow {
              top: calc(50% - ${BOTTOM_PADDING_MEDIUM / 2}px);
            }
          }

          .MobileCarousel__left-arrow--active {
            opacity: 1;
            visibility: visible;
          }

          @media ${small} {
            .MobileCarousel__right-arrow--active-small {
              opacity: 1;
              visibility: visible;
            }
          }

          @media ${mediumUp} {
            .MobileCarousel__right-arrow--active-medium {
              opacity: 1;
              visibility: visible;
            }
          }

          .MobileCarousel__right-arrow {
            right: ${Theme.get('spacing') * 8}px;
          }

          .MobileCarousel__left-arrow {
            left: ${Theme.get('spacing') * 8}px;
          }
        `}</style>
        <style jsx global>
          {`
            .MobileCarousel .Carousel {
              overflow: visible !important;
            }

            .MobileCarousel .Carousel > div {
              display: flex;
            }

            .MobileCarousel .Carousel > div > div {
              flex: 1;
            }

            .MobileCarousel .Carousel::after {
              content: '';
              display: table;
            }
          `}
        </style>
      </div>
    );
  }
}

MobileCarousel.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      categoryCards: PropTypes.arrayOf(
        PropTypes.shape({
          productName: PropTypes.string.isRequired,
          tagline: PropTypes.string.isRequired,
          taglineIcon: PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string,
          }).isRequired,
          image: PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string,
          }).isRequired,
          ctaLinks: PropTypes.arrayOf(
            PropTypes.shape({
              url: PropTypes.string.isRequired,
              text: PropTypes.string.isRequired,
              openInNewWindow: PropTypes.bool.isRequired,
            })
          ).isRequired,
        }).isRequired
      ),
    }).isRequired
  ).isRequired,
  trackCTAClick: PropTypes.func.isRequired,
  handleSlideSelect: PropTypes.func.isRequired,
  handleCategorySelect: PropTypes.func.isRequired,
  activeCategoryIndex: PropTypes.number.isRequired,
  activeSlideIndex: PropTypes.number.isRequired,
};

export default MobileCarousel;
